/*
 * Copyright (c) 2020 General Electric Company. All rights reserved.
 *
 * The copyright to the computer software herein is the property of
 * General Electric Company. The software may be used and/or copied only
 * with the written permission of General Electric Company or in accordance
 * with the terms and conditions stipulated in the agreement/contract
 * under which the software has been supplied.
 */

import { Injectable } from '@angular/core';
import { HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Logger, MessageService } from '@core/lib/services';
import * as _ from 'underscore';
import { TranslateService } from '@ngx-translate/core';
import { NotifyService } from '@app/common/notify.service';
import { HttpService } from './http.service';
import { StateUpdate } from '@app/data-model/state-update';
import { appConstants } from '@app/common/app-constants';
import { Router } from '@angular/router';
import { UtilService } from '@app/util/util.service';
import { CookieService } from 'ngx-cookie-service';

const log = new Logger('ProxyDataService');

@Injectable({
  providedIn: 'root'
})
export class ProxyDataService {
  private proxyUrl = '/dataController/proxy';
  private expectedHttpErrorCodes = [403, 404, 409, 422, 501, 503, 573];
  private suppressDataServiceWarnings = true;
  private encryptedCredentials: any = undefined;
  private reconnectModal: any = undefined;

  constructor(
    private httpClient: HttpService,
    private translate: TranslateService,
    private notify: NotifyService,
    private router: Router,
    private utilService: UtilService,
    private messageService: MessageService,
    private cookieService: CookieService
  ) {}

  setEncryptedCredentials() {
    if (this.encryptedCredentials === undefined) {
      (async () => {
        try {
          this.encryptedCredentials = await this.httpGet(appConstants.appURLs.encryptedPassword);
        } catch (err) {
          log.error('Error occurred while getting EncryptedCredentials, Error -', JSON.stringify(err));
        }
      })();
    }
  }

  get(url: string | URL, params?: any) {
    const httpOptions = this.getHttpOptions(url, params);
    return new Promise((resolve, reject) => {
      this.httpClient
        .get(this.proxyUrl, httpOptions)
        .toPromise()
        .then(
          (resp: any) => {
            resolve((resp && resp.data) || resp);
          },
          (error: HttpErrorResponse) => {
            reject(this.processDataServiceError(error, null, url, undefined));
          }
        );
    });
  }

  getById(url: string, id?: string) {
    const httpOptions = this.getHttpOptions(url, undefined, id);
    return new Promise((resolve, reject) => {
      this.httpClient
        .get(this.proxyUrl, httpOptions)
        .toPromise()
        .then(
          (resp: any) => {
            resolve((resp && resp.data) || resp);
          },
          (error: HttpErrorResponse) => {
            reject(this.processDataServiceError(error, null, url, undefined));
          }
        );
    });
  }

  getByIdParams(url: string, id: string, params?: any) {
    const httpOptions = this.getHttpOptions(url, params, id);
    return new Promise((resolve, reject) => {
      this.httpClient
        .get(this.proxyUrl, httpOptions)
        .toPromise()
        .then(
          (resp: any) => {
            resolve((resp && resp.data) || resp);
          },
          (error: HttpErrorResponse) => {
            reject(this.processDataServiceError(error, null, url, undefined));
          }
        );
    });
  }

  post(url: string | URL, data: any, type?: any) {
    const httpOptions = this.getHttpOptions(url, undefined, undefined, type);
    data = data ? data : null;
    return new Promise((resolve, reject) => {
      this.httpClient
        .post(this.proxyUrl, data, httpOptions)
        .toPromise()
        .then(
          (resp: any) => {
            resolve((resp && resp.data) || resp);
          },
          (error: HttpErrorResponse) => {
            reject(this.processDataServiceError(error, null, url, data));
          }
        );
    });
  }

  create(url: string | URL, data: any, type?: any) {
    const httpOptions = this.getHttpOptions(url, undefined, undefined, type);
    data = _.extend({}, data ? data : null);
    return new Promise((resolve, reject) => {
      this.httpClient
        .post(this.proxyUrl, data, httpOptions)
        .toPromise()
        .then(
          (resp: any) => {
            resolve((resp && resp.data) || resp);
          },
          (error: HttpErrorResponse) => {
            reject(this.processDataServiceError(error, null, url, undefined));
          }
        );
    });
  }

  createList(url: string | URL, data: any) {
    const httpOptions = this.getHttpOptions(url, undefined);
    return new Promise((resolve, reject) => {
      this.httpClient
        .post(this.proxyUrl, data, httpOptions)
        .toPromise()
        .then(
          (resp: any) => {
            resolve((resp && resp.data) || resp);
          },
          (error: HttpErrorResponse) => {
            reject(this.processDataServiceError(error, null, url, undefined));
          }
        );
    });
  }

  update(url: string, id: any, data?: any) {
    const httpOptions = this.getHttpOptions(url, undefined, id);
    data = _.extend({}, data ? data : null);
    return new Promise((resolve, reject) => {
      this.httpClient
        .put(this.proxyUrl, data, httpOptions)
        .toPromise()
        .then(
          (resp: any) => {
            resolve((resp && resp.data) || resp);
          },
          (error: HttpErrorResponse) => {
            reject(this.processDataServiceError(error, id, url, undefined));
          }
        );
    });
  }

  remove(url: string) {
    const httpOptions = {
      headers: new HttpHeaders({
        'Service-End-Point': this.getServiceEndPoint(url, undefined, undefined),
        'External-Login': this.utilService.externalLoginApp !== undefined ? 'true' : 'false'
      })
    };
    return new Promise((resolve, reject) => {
      this.httpClient
        .delete(this.proxyUrl, httpOptions)
        .toPromise()
        .then(
          (resp: any) => {
            resolve((resp && resp.data) || resp);
          },
          (error: HttpErrorResponse) => {
            reject(this.processDataServiceError(error, null, url, undefined));
          }
        );
    });
  }

  httpGet(url: string, httpOptions?: any): Promise<any> {
    if (this.utilService.externalLoginApp !== undefined) httpOptions = this.addExternalLoginHeader(httpOptions);
    if (url === appConstants.appURLs.jwtToken) {
      return new Promise((resolve, reject) => {
        this.httpClient
          .get(url, httpOptions)
          .toPromise()
          .then(
            (resp: any) => {
              resolve(resp);
            },
            (error: HttpErrorResponse) => {
              reject(this.processDataServiceError(error, null, url, undefined));
            }
          );
      });
    } else {
      return httpOptions ? this.httpClient.get(url, httpOptions).toPromise() : this.httpClient.get(url).toPromise();
    }
  }

  httpPost(url: string, data: any | null, httpOptions?: any): Promise<any> {
    if (this.utilService.externalLoginApp !== undefined) httpOptions = this.addExternalLoginHeader(httpOptions);
    return httpOptions
      ? this.httpClient.post(url, data, httpOptions).toPromise()
      : this.httpClient.post(url, data).toPromise();
  }

  httpPut(url: string, body: any | null, httpOptions?: any): Promise<any> {
    if (this.utilService.externalLoginApp !== undefined) httpOptions = this.addExternalLoginHeader(httpOptions);
    return httpOptions
      ? this.httpClient.put(url, body, httpOptions).toPromise()
      : this.httpClient.put(url, body).toPromise();
  }

  httpDelete(url: string, httpOptions?: any): Promise<any> {
    if (this.utilService.externalLoginApp !== undefined) httpOptions = this.addExternalLoginHeader(httpOptions);
    return httpOptions ? this.httpClient.delete(url, httpOptions).toPromise() : this.httpClient.delete(url).toPromise();
  }

  addExternalLoginHeader(httpOptions) {
    if (httpOptions) {
      if (httpOptions?.headers) {
        httpOptions.headers = httpOptions.headers.append('External-Login', 'true');
      } else {
        httpOptions.headers = new HttpHeaders({ 'External-Login': 'true' });
      }
    } else {
      httpOptions = { headers: new HttpHeaders({ 'External-Login': 'true' }) };
    }
    return httpOptions;
  }

  getWorkItemStates(workItemIds, userId: string) {
    return new Promise((resolve, reject) => {
      this.get(appConstants.appURLs.workItemState, { id: workItemIds }).then(
        (statesResponse: any) => {
          const updates: StateUpdate[] = [];
          // update the workitems through the page or through this list.
          for (const stateResponse of statesResponse) {
            updates.push(new StateUpdate(stateResponse.workitemId.toString(), stateResponse, userId));
          }
          resolve(updates);
        },
        error => {
          reject(error);
        }
      );
    });
  }

  setSuppressDataServiceWarnings(val: boolean) {
    this.suppressDataServiceWarnings = val;
  }

  public transformUrlParams(urlAttributes) {
    const urlParams = [];
    if (!_.isUndefined(urlAttributes) && !_.isEmpty(urlAttributes)) {
      Object.keys(urlAttributes).forEach(key => {
        urlParams.push(key + '=' + urlAttributes[key]);
      });
    }
    return urlParams.join('&');
  }

  private getHttpOptions(url: string | URL, params: any, id?: string, responseType?: any) {
    return {
      responseType: !_.isUndefined(responseType) ? responseType : 'json',
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        'Service-End-Point': this.getServiceEndPoint(url, params, id),
        'External-Login': this.utilService.externalLoginApp !== undefined ? 'true' : 'false'
      })
    };
  }

  private getServiceEndPoint(url: string | URL, params: any, id: string) {
    let serviceEndPoint = this.utilService.getAppBaseUrl() + '/' + url;
    if (url instanceof URL) {
      serviceEndPoint = url.toString();
    }
    if (id) {
      serviceEndPoint += '/' + id;
    }
    params = this.transformUrlParams(params);
    if (params !== '') {
      serviceEndPoint += '?' + params;
    }
    serviceEndPoint = encodeURI(serviceEndPoint);
    return serviceEndPoint;
  }

  processDataServiceError(error: HttpErrorResponse, id: string, url: string | URL, data) {
    const isExternalAppLaunch = this.utilService.externalLoginApp !== undefined;
    const message = isExternalAppLaunch
      ? this.translate.instant('imagingDesktop.error.sessionDisConnect.ccwWorkflow')
      : this.translate.instant('imagingDesktop.error.sessiontimeout');
    const noButtonLabel = isExternalAppLaunch
      ? this.translate.instant('label.OK')
      : this.translate.instant('label.logout');

    if (this.cookieService.get('sessionDisconectfail') === 'true') {
      this.cookieService.set('sessionDisconectfail', 'false');
      this.handleServerError();
    } else if (error !== null && error.status >= 400) {
      log.error(
        'Service Error for  [' +
          url +
          '] : with status' +
          error.status +
          ' - status text : ' +
          error.statusText +
          ', Message - ' +
          error.message
      );

      if (error.status === 401) {
        if (
          !this.reconnectModal &&
          !data?.configValue?.includes('recordmanager') &&
          url !== 'recent-exams/tabs/delete'
        ) {
          this.reconnectModal = this.notify
            .decision(message, {
              headerContent: this.translate.instant('imagingDesktop.error.sessiontimeout.Title'),
              content: message,
              yesButtonLabel: this.translate.instant('label.continue'),
              noButtonLabel: noButtonLabel,
              isBlockModal: true,
              disableNoBtn: false,
              disableHeaderCloseBtn: true,
              isDecisionModal: !isExternalAppLaunch,
              enableYesBtn: true
            })
            .then(value => {
              if (value === 'NO') {
                localStorage.currentUserLoggedIn = 'false';
                this.messageService.broadcast('signOutOnSessionTimeout');
              } else {
                if (this.utilService.getSystemProp()['SSO_Flag'] === 'true') {
                  this.messageService.broadcast('ssoSessionReconect');
                } else {
                  (async () => {
                    try {
                      this.messageService.broadcast('sessionContinue');
                      await this.httpPost(
                        appConstants.appURLs.login,
                        {
                          loginUserName: encodeURIComponent(this.encryptedCredentials.loginUserName),
                          loginPassword: encodeURIComponent(this.encryptedCredentials.encryptedPassword),
                          isEncryptedPassword: true
                        },
                        {
                          headers: new HttpHeaders({
                            'Content-Type': 'application/json'
                          })
                        }
                      );
                      this.messageService.broadcast('signInAfterSessionTimeout');
                    } catch (err) {
                      log.error('Error occurred while establishing user session, Error -', JSON.stringify(err));
                      //Handle reauthentication failure after session disconnect.
                      this.messageService.broadcast('signInAfterSessionTimeout');
                      this.handleServerError();
                    }
                  })();
                }
              }
            })
            .finally(() => {
              this.reconnectModal = undefined;
            });
        }
      } else if (-1 === this.expectedHttpErrorCodes.indexOf(error.status)) {
        log.debug('SuppressUnknownDataAccessErrorNotification is set as ' + this.suppressDataServiceWarnings);
        if (this.suppressDataServiceWarnings) {
          log.error(
            'An unknown error has occurred with the data service. ' +
              'Click on another patient or worklist to see if this error still occurs. If this problem persists, ' +
              'close your browser and contact the system manager.'
          );
        } else {
          if (id !== null && typeof id === 'string' && id.includes('unreject-image')) {
            log.error('Service Error for  [ Unreject Images ]');
          } else {
            this.notify.error(this.translate.instant('imagingDesktop.error.DataService'));
          }
        }
      }
    }
    return {
      message: error.message,
      status: error.status,
      responseText: error.statusText,
      URL: url,
      error: error.error
    };
  }

  private handleServerError() {
    this.reconnectModal = this.notify.decision(this.translate.instant('sso.session.disconnect.failed'), {
      disableHeaderCloseBtn: true,
      headerContent: this.translate.instant('masthead.error.serverErrorHeading'),
      disableNoBtn: true,
      yesButtonLabel: this.translate.instant('label.OK'),
      isDecisionModal: true,
      enableYesBtn: true
    });
    this.reconnectModal.finally(() => {
      this.messageService.broadcast('signOutOnSessionTimeout');
    });
  }
}
